import { toast } from "react-toastify";

import { ProfileService, UserService } from "@/services/index.js";
import { getAuthorizedUser } from "@/store/auth/auth.slice.js";
import { type ThunkDispatch } from "@/store/hooks.js";
import type { PhotoEditSettings, PhotoSettings, User } from "@/store/v2/currentUser/selectors.js";

import type { MyProfileFormData } from "../types.js";
import flattenDirtyFields, { type NestedDirtyFields } from "./flattenDirtyFields.js";

const restoreUrl = (url: string) => {
  const suffix = "_inactive";
  if (url.endsWith(suffix)) {
    return url.slice(0, -suffix.length);
  }
  return url;
};

type FlatDirtyFields = Record<string, boolean>;

export const defaultSettings: PhotoEditSettings = {
  aspect: 1,
  crop: { x: 0, y: 0 },
  rotation: 0,
  zoom: 1,
  tiltToggle: false,
  tiltMin: -20,
  tiltMax: 20,
  croppedAreaPixels: null,
};

export const DELETE_TYPE = {
  SOFT: "soft",
  HARD: "hard",
} as const;

type DeleteType = (typeof DELETE_TYPE)[keyof typeof DELETE_TYPE];

export const restorePhoto = async (user: User, dispatch: ThunkDispatch) => {
  await UserService.updateUser(user.id, {
    photoUrl: restoreUrl(user.photoUrl),
    originalPhotoUrl: restoreUrl(user.originalPhotoUrl),
    photoSettings: user.photoSettings,
  });
  dispatch(getAuthorizedUser());
};

/**
 * Soft delete marks the photo as inactive by appending "_inactive" to photoUrl and originalPhotoUrl in the database, making them inaccessible in S3.
 * Hard delete completely removes the photo by setting photoUrl and originalPhotoUrl to null in the database and deleting the photo from S3.
 */
export const deletePhoto = async (user: User, deleteType: DeleteType, dispatch: ThunkDispatch) => {
  await UserService.updateUser(user.id, {
    photoUrl: user.photoUrl,
    originalPhotoUrl: user.originalPhotoUrl,
    deleteType,
  });
  dispatch(getAuthorizedUser());
};
/**
 * Handles photo upload success.
 */
export const onPhotoUploadSuccess = async (
  photoUrl: string,
  originalPhotoUrl: string,
  photoSettings: PhotoSettings,
  dispatch: ThunkDispatch,
) => {
  try {
    await ProfileService.updateProfile({ photoUrl, originalPhotoUrl, photoSettings });
    dispatch(getAuthorizedUser());
  } catch (error) {
    console.error("Failed to update profile photo:", error);
    throw error;
  }
};

/**
 * Handles profile updates.
 */
export const saveProfileUpdateWithEmail = async (
  data: MyProfileFormData,
  dirtyFields: NestedDirtyFields<MyProfileFormData>,
  getDataToUpdate: (dirtyFields: FlatDirtyFields, data: MyProfileFormData) => Partial<MyProfileFormData>,
  logout: () => Promise<void>,
  dispatch: ThunkDispatch,
): Promise<void> => {
  try {
    const flattenedDirtyFields = flattenDirtyFields(dirtyFields);

    const emailChanged = !!flattenedDirtyFields.email;

    const updateData = getDataToUpdate(flattenedDirtyFields, data);

    const { err, status } = await ProfileService.updateProfile(updateData);

    if (!err && status === "succeeded") {
      dispatch(getAuthorizedUser());
      toast.success("Updated profile successfully!");

      if (emailChanged) {
        await logout();
      }
    } else {
      throw err;
    }
  } catch (e) {
    toast.error("Oops something went wrong.");
    console.error(e);
    throw e;
  }
};

/**
 * Extracts the fields to update based on the `dirtyFields` object.
 */
export const getDataToUpdate = (dirtyFields: FlatDirtyFields, data: MyProfileFormData): Partial<MyProfileFormData> => {
  return Object.keys(dirtyFields).reduce<Partial<MyProfileFormData>>((result, key) => {
    const fieldKey = key as keyof MyProfileFormData;

    const value = data[fieldKey];

    if (value !== undefined) {
      (result[fieldKey] as unknown) = value;
    }

    return result;
  }, {});
};

/**
 * Formats user phone data.
 */
export const formatPhone = (phone?: { code?: string; iso?: string; number?: string }) =>
  phone
    ? { code: phone.code || "", iso: phone.iso || "", number: phone.number || "" }
    : { code: "", iso: "", number: "" };

/**
 * Formats a phone number string into a readable format.
 */
export const formatPhoneNumber = (number: string = ""): string =>
  number.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3") || "";

/**
 * Formats a phone number with country code into a readable format
 */
export const formatPhoneNumberWithCountryCode = (phone?: { code?: string; number?: string }): string => {
  if (!phone || !phone.code || !phone.number) return "";

  const cleanedNumber = phone.number.replace(/\D/g, "");
  if (cleanedNumber.length !== 10) return `${phone.code} ${phone.number}`;

  return `${phone.code} (${cleanedNumber.slice(0, 3)}) ${cleanedNumber.slice(3, 6)}-${cleanedNumber.slice(6)}`;
};

/**
 * Verifies if the given email address is unique.
 */
export const verifyUser = async (value: string): Promise<boolean> => {
  try {
    const { resp } = await UserService.getUsers({
      offset: 0,
      limit: 1,
      email: value,
    });
    return !resp.data.length;
  } catch {
    return false;
  }
};

export const DEFAULT_PROFILE_FIELDS = {
  name: "",
  email: "",
  workPhone: { code: "", iso: "", number: "" },
  cellPhone: { code: "", iso: "", number: "" },
  generalNotificationsSmsEnabled: false,
  generalNotificationsEmailEnabled: false,
  contentNotificationsSmsEnabled: false,
  contentNotificationsEmailEnabled: false,
  engagementNotificationsSmsEnabled: false,
  engagementNotificationsEmailEnabled: false,
  displayWorkPhone: false,
  displayEmail: false,
};
